import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { coursesService } from '../../services/api';
import type { AdminCourseLessonUpdate } from '../../types';

const BADGES: Array<{ id: AdminCourseLessonUpdate['badge']; label: string }> = [
  { id: 'NEW', label: 'جدید' },
  { id: 'IMPROVEMENT', label: 'بهبود' },
  { id: 'ANNOUNCEMENT', label: 'اطلاعیه' },
];

const KINDS: Array<{ id: AdminCourseLessonUpdate['contentKind']; label: string }> = [
  { id: 'VIDEO', label: 'ویدیو' },
  { id: 'AUDIO', label: 'صوت' },
  { id: 'BOTH', label: 'ویدیو + صوت' },
];

function emptyForm(): {
  title: string;
  description: string;
  badge: AdminCourseLessonUpdate['badge'];
  contentKind: AdminCourseLessonUpdate['contentKind'];
  durationLabel: string;
  versionLabel: string;
  occurredAt: string;
  sortOrder: string;
  videoUrl: string;
  audioUrl: string;
  published: boolean;
  videoFile: File | null;
  audioFile: File | null;
} {
  return {
    title: '',
    description: '',
    badge: 'NEW',
    contentKind: 'VIDEO',
    durationLabel: '',
    versionLabel: '',
    occurredAt: '',
    sortOrder: '0',
    videoUrl: '',
    audioUrl: '',
    published: true,
    videoFile: null,
    audioFile: null,
  };
}

function toFormDto(f: ReturnType<typeof emptyForm>): FormData {
  const fd = new FormData();
  fd.append('title', f.title.trim());
  fd.append('description', f.description.trim());
  fd.append('badge', f.badge);
  fd.append('contentKind', f.contentKind);
  if (f.durationLabel.trim()) fd.append('durationLabel', f.durationLabel.trim());
  if (f.versionLabel.trim()) fd.append('versionLabel', f.versionLabel.trim());
  if (f.occurredAt.trim()) fd.append('occurredAt', new Date(f.occurredAt).toISOString());
  fd.append('sortOrder', f.sortOrder || '0');
  if (f.videoUrl.trim()) fd.append('videoUrl', f.videoUrl.trim());
  if (f.audioUrl.trim()) fd.append('audioUrl', f.audioUrl.trim());
  fd.append('published', f.published ? 'true' : 'false');
  if (f.videoFile) fd.append('video', f.videoFile);
  if (f.audioFile) fd.append('audio', f.audioFile);
  return fd;
}

/** همان بدنهٔ کامل؛ برای PATCH با multipart همه فیلدهای متنی همراه با فایل‌های اختیاری ارسال می‌شوند. */
function toFullPatchFd(f: ReturnType<typeof emptyForm>): FormData {
  return toFormDto(f);
}

function axiosMessageToText(msg: unknown, fallback: string): string {
  if (typeof msg === 'string' && msg.trim()) return msg;
  if (Array.isArray(msg)) return msg.map(String).join(' — ') || fallback;
  return fallback;
}

/** ۴۱۳ از nginx می‌آید (client_max_body_size) و بدنهٔ JSON ندارد؛ پیام عمومی گمراه‌کننده است. */
function uploadErrorText(err: unknown, fallback: string): string {
  const e = err as { response?: { status?: number; data?: { message?: unknown } }; code?: string };
  if (e?.response?.status === 413) {
    return 'حجم فایل از حد مجاز سرور بیشتر است. تنظیم client_max_body_size در nginx باید افزایش پیدا کند.';
  }
  if (e?.code === 'ERR_NETWORK') {
    return 'ارتباط با سرور در حین آپلود قطع شد. اگر فایل حجیم است، اتصال و محدودیت حجم سرور را بررسی کنید.';
  }
  return axiosMessageToText(e?.response?.data?.message, fallback);
}

/** نوع محتوا را با فایل‌های واقعاً انتخاب‌شده هماهنگ می‌کند تا در سایت دکمهٔ پخش درست ساخته شود. */
function syncContentKind(
  current: AdminCourseLessonUpdate['contentKind'],
  hasVideo: boolean,
  hasAudio: boolean,
): AdminCourseLessonUpdate['contentKind'] {
  if (hasVideo && hasAudio) return 'BOTH';
  if (hasAudio && !hasVideo) return current === 'BOTH' ? 'BOTH' : 'AUDIO';
  if (hasVideo && !hasAudio) return current === 'BOTH' ? 'BOTH' : 'VIDEO';
  return current;
}

type Props = { courseId: string };

export default function CourseLessonUpdatesTab({ courseId }: Props) {
  const [rows, setRows] = useState<AdminCourseLessonUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await coursesService.getLessonUpdatesAdmin(courseId);
      setRows(Array.isArray(data) ? data : []);
    } catch (e: unknown) {
      setError((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'خطا در بارگذاری');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    load();
  }, [load]);

  const sorted = useMemo(() => [...rows].sort((a, b) => b.sortOrder - a.sortOrder), [rows]);

  const resetForm = () => {
    setForm(emptyForm());
    setEditingId(null);
    setCreating(false);
  };

  const startEdit = (r: AdminCourseLessonUpdate) => {
    setEditingId(r.id);
    setCreating(false);
    const d = new Date(r.occurredAt);
    const local = Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 16);
    setForm({
      title: r.title,
      description: r.description || '',
      badge: r.badge,
      contentKind: r.contentKind,
      durationLabel: r.durationLabel || '',
      versionLabel: r.versionLabel || '',
      occurredAt: local,
      sortOrder: String(r.sortOrder),
      videoUrl: r.videoUrl || '',
      audioUrl: r.audioUrl || '',
      published: r.published,
      videoFile: null,
      audioFile: null,
    });
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('عنوان الزامی است');
      return;
    }
    setBusyId('_new');
    setError('');
    try {
      const fd = toFormDto(form);
      await coursesService.createLessonUpdate(courseId, fd);
      resetForm();
      await load();
    } catch (err: unknown) {
      setError(uploadErrorText(err, 'خطا در ثبت'));
    } finally {
      setBusyId(null);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    const original = rows.find((row) => row.id === editingId);
    if (!original) return;
    setBusyId(original.id);
    setError('');
    try {
      const fd = toFullPatchFd(form);
      await coursesService.patchLessonUpdate(courseId, original.id, fd);
      resetForm();
      await load();
    } catch (err: unknown) {
      setError(uploadErrorText(err, 'خطا در ذخیره'));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('این آپدیت حذف شود؟')) return;
    setBusyId(id);
    try {
      await coursesService.deleteLessonUpdate(courseId, id);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5 text-right">
      <div className="rounded-xl border border-indigo-200 bg-gradient-to-bl from-[#f4f4ff] to-white p-4">
        <p className="text-sm font-bold text-[#1C3B9B]">آپدیت جلسات (ثبت دستی)</p>
        <p className="text-xs text-slate-600 mt-1 leading-relaxed">
          همهٔ آپدیت‌ها را <strong className="text-slate-800">خودتان اینجا اضافه و ویرایش می‌کنید</strong>؛ هیچ موردی از سرفصل‌ها
          یا جلسات دوره به‌صورت خودکار ساخته نمی‌شود. هر ردیف با دکمهٔ «ثبت آپدیت» / «ذخیرهٔ تغییرات» همان‌جا ذخیره می‌شود.
          برای نمایش در سایت، گزینهٔ «منتشر شده» را بزنید. فیلدها: برچسب، نوع محتوا، مدت و نمایشی، نسخه، تاریخ، لینک
          یا آپلود ویدیو/صوت.
        </p>
      </div>

      {!creating && editingId === null && (
        <button
          type="button"
          onClick={() => {
            resetForm();
            setCreating(true);
            setForm(emptyForm());
          }}
          className="inline-flex items-center gap-2 rounded-xl bg-[#1C3B9B] text-white px-4 py-2 text-sm font-bold shadow-sm hover:bg-[#153088]"
        >
          + افزودن آپدیت دستی
        </button>
      )}

      {(creating || editingId) && (
        <form
          onSubmit={(e) => (editingId ? handleUpdate(e) : handleCreate(e))}
          className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3 shadow-sm"
        >
          <div className="flex justify-between items-center border-b pb-2">
            <span className="text-sm font-bold text-slate-800">{editingId ? 'ویرایش آپدیت' : 'آپدیت جدید'}</span>
            <button type="button" className="text-xs text-red-600" onClick={resetForm}>
              انصراف
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">عنوان</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                required
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">توضیح / یادداشت</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm min-h-[80px]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">برچسب وضعیت</label>
              <select
                value={form.badge}
                onChange={(e) => setForm({ ...form, badge: e.target.value as typeof form.badge })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              >
                {BADGES.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">نوع محتوا در سایت</label>
              <select
                value={form.contentKind}
                onChange={(e) => setForm({ ...form, contentKind: e.target.value as typeof form.contentKind })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              >
                {KINDS.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">مدت (نمایشی، مثل ۲۸:۰۰)</label>
              <input
                value={form.durationLabel}
                onChange={(e) => setForm({ ...form, durationLabel: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="۲۸:۰۰"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">نسخه</label>
              <input
                value={form.versionLabel}
                onChange={(e) => setForm({ ...form, versionLabel: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="v1.4"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">تاریخ رویداد</label>
              <input
                type="datetime-local"
                value={form.occurredAt}
                onChange={(e) => setForm({ ...form, occurredAt: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">ترتیب نمایش (بزرگتر = بالاتر)</label>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                min={0}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">لینک اشتراک ویدیو (آپارات و …)</label>
              <input
                value={form.videoUrl}
                onChange={(e) => setForm({ ...form, videoUrl: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dir-ltr text-left"
                placeholder="https://www.aparat.com/v/..."
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-slate-700 mb-1">لینک مستقیم صوت (اختیاری)</label>
              <input
                value={form.audioUrl}
                onChange={(e) => setForm({ ...form, audioUrl: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dir-ltr text-left"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">فایل ویدیو آپلود</label>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setForm((prev) => ({
                    ...prev,
                    videoFile: file,
                    contentKind: syncContentKind(prev.contentKind, Boolean(file), Boolean(prev.audioFile)),
                  }));
                }}
                className="w-full text-sm"
              />
              {form.videoFile && (
                <p className="mt-1 text-[11px] text-slate-500">
                  {form.videoFile.name} — {(form.videoFile.size / (1024 * 1024)).toFixed(1)} MB
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">فایل صوت آپلود</label>
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setForm((prev) => ({
                    ...prev,
                    audioFile: file,
                    contentKind: syncContentKind(prev.contentKind, Boolean(prev.videoFile), Boolean(file)),
                  }));
                }}
                className="w-full text-sm"
              />
              {form.audioFile && (
                <p className="mt-1 text-[11px] text-slate-500">
                  {form.audioFile.name} — {(form.audioFile.size / (1024 * 1024)).toFixed(1)} MB
                </p>
              )}
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <input
                id="lu-published"
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                className="h-4 w-4 rounded"
              />
              <label htmlFor="lu-published" className="text-sm text-slate-700">
                منتشر شده (در سایت دیده شود)
              </label>
            </div>
          </div>
          <button
            type="submit"
            disabled={busyId !== null}
            className="rounded-xl bg-emerald-600 text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
          >
            {busyId !== null ? 'در حال ذخیره…' : editingId ? 'ذخیرهٔ تغییرات' : 'ثبت آپدیت'}
          </button>
        </form>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
      )}

      {loading ? (
        <p className="text-sm text-slate-500">در حال بارگذاری…</p>
      ) : (
        <div className="space-y-2">
          {sorted.map((r) => (
            <div
              key={r.id}
              className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 flex flex-wrap gap-3 justify-between items-start"
            >
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-sm font-bold text-slate-900">{r.title}</p>
                <p className="text-xs text-slate-600 line-clamp-2">{r.description || '—'}</p>
                <p className="text-[11px] text-slate-500">
                  نوع {KINDS.find((k) => k.id === r.contentKind)?.label} · برچسب {BADGES.find((b) => b.id === r.badge)?.label}{' '}
                  · منتشر {r.published ? 'بله' : 'خیر'}
                </p>
                {(r.videoFileUrl || r.videoUrl || r.audioFileUrl || r.audioUrl) && (
                  <p className="text-[10px] text-emerald-700">
                    رسانه: {r.videoFileUrl ? 'ویدیو فایل' : ''}{' '}
                    {r.videoUrl ? 'لینک ویدیو' : ''}{' '}
                    {r.audioFileUrl ? 'صوت فایل' : ''}{' '}
                    {r.audioUrl ? 'لینک صوت' : ''}
                  </p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  disabled={busyId !== null || creating || editingId !== null}
                  onClick={() => startEdit(r)}
                  className="text-xs font-semibold text-[#1C3B9B] px-2 py-1 border border-[#1C3B9B]/30 rounded-lg hover:bg-white"
                >
                  ویرایش
                </button>
                <button
                  type="button"
                  disabled={busyId !== null}
                  onClick={() => handleDelete(r.id)}
                  className="text-xs font-semibold text-red-600 px-2 py-1 border border-red-200 rounded-lg hover:bg-red-50"
                >
                  حذف
                </button>
              </div>
            </div>
          ))}
          {sorted.length === 0 && !creating && <p className="text-sm text-slate-500">هنوز آپدیتی ثبت نشده است.</p>}
        </div>
      )}
    </div>
  );
}
